/**
 * Construction Labor Cost Engine layout route.
 *
 * Renders a side-nav with links to the construction-labor sub-pages,
 * gated by the construction pack flag on the current entity.
 */
import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

import { myEntitiesQueryOptions } from "@/lib/queries";
import { usePayrollL2Shortcuts } from "@/lib/usePayrollL2Shortcuts";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2")({
  component: PayrollL2Layout,
});

const SUB_NAV = [
  { to: "/payroll-l2/timesheet-feed", label: "Timesheet Feed" },
  { to: "/payroll-l2/flagged", label: "Flagged Events" },
  { to: "/payroll-l2/crafts", label: "Crafts & Rates" },
  { to: "/payroll-l2/burden", label: "Burden" },
  { to: "/payroll-l2/workers-comp", label: "Workers Comp" },
  { to: "/payroll-l2/ot-rules", label: "OT Rules" },
  { to: "/payroll-l2/allowances", label: "Allowances" },
  { to: "/payroll-l2/equipment", label: "Equipment" },
  { to: "/payroll-l2/expenses", label: "Expenses" },
  { to: "/payroll-l2/differentials", label: "Differentials" },
  { to: "/payroll-l2/anomalies", label: "AI Anomalies" },
  { to: "/payroll-l2/retro-pay", label: "Retro Pay" },
  { to: "/payroll-l2/forms", label: "Compliance Forms" },
] as const;

function PayrollL2Layout() {
  const matchRoute = useMatchRoute();
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? true;

  // Register G-prefix shortcuts for the whole construction-labor section lifetime
  usePayrollL2Shortcuts();

  if (!constructionEnabled) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        The Construction pack is not enabled for this entity.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1">
      {/* Side nav */}
      <aside className="w-52 shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col py-4 px-2 gap-0.5">
        <p className="px-3 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
          Labor Cost Engine
        </p>
        <div className="px-3 mb-2">
          <HelpHint docPath="payroll-l2/README.md" label="Construction labor overview" />
        </div>
        {SUB_NAV.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const active = matchRoute({ to: item.to as any, fuzzy: true });
          return (
            <Link
              key={item.to}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to={item.to as any}
              className={clsx(
                "px-3 py-2 rounded-md text-sm transition",
                active
                  ? "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 font-medium"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800",
              )}
            >
              {item.label}
            </Link>
          );
        })}
      </aside>

      {/* Page content */}
      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
